import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { combineLatest, finalize } from 'rxjs';
import { AlertModel, AlertType } from 'src/app/models/alert.model';
import { PreviewSentEmailDialogComponent } from 'src/app/modules/correspondence/components/preview-sent-email-dialog/preview-sent-email-dialog.component';
import { AlertsService } from 'src/app/services/alerts.service';
import { Constants } from 'src/app/services/constants';
import { EmailService } from 'src/app/services/email.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AlertBucket, Notifications } from '../header-actions/header-actions.component';
import { MessageService } from 'src/app/services/message.service';

@Component({
    selector: 'notifications-list',
    templateUrl: './notifications-list.component.html',
    styleUrls: ['./notifications-list.component.scss'],
    standalone: false
})
export class NotificationsListComponent implements OnInit {

  @Input()
  modal = false;

  @Input()
  data: Notifications = new Notifications();

  @Output()
  numberOfAlertsChanged: EventEmitter<NumberOfAlertsChangedEvent> = new EventEmitter<NumberOfAlertsChangedEvent>();

  @Output()
  clickedGotoDetails: EventEmitter<AlertModel> = new EventEmitter<AlertModel>();

  notifications: AlertModel[] = [];
  numberOfRecords = 20;

  alertsLoading: boolean = false;

  selectedAlertBuckets: AlertType[] = [];
  availableBucketsForFiltering: AlertBucket[] = [];

  cleaningAll: boolean = false;

  constructor(
    private readonly _alertsService: AlertsService,
    private readonly _notificationService: NotificationService,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _modalService: NgbModal,
    private readonly _emailService: EmailService,
    private readonly _messageService: MessageService
  ) { }

  onScroll(event: Event): void {
    if (this.shouldLoadMore(event)) {
      this.numberOfRecords = this.numberOfRecords + 20;
      this.loadMoreRecords();
    }
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.numberOfRecords = 20;
    setTimeout(() => {
      this.refreshFilters();
      this.onFilterChanged();
    });
  }

  shouldLoadMore = (event: any): boolean => {
    if (
      event.target.offsetHeight + event.target.scrollTop + 50 >=
      event.target.scrollHeight
    ) {
      return true;
    }
  }

  gotoDetails = (alert: AlertModel) => {

    this.clickedGotoDetails.emit(alert);
    // if (alert && alert.alertType == 'BorrowerMessageAlert') {
    //   //open the message conversation in the chats panel
    // }
    // else if (alert && alert.applicationId && alert.taskId) {
    //   //Open the task in the side panel
    // }
    // else if (alert && alert.applicationId && alert.borrowerId) {
    //   this._router.navigate([
    //     `admin/app-details/${alert.applicationId}`,
    //   ]);
    // }
    // else if (alert && alert.leadId) {
    //   //route to leads page and open lead in modal
    // }
  }

  deleteAlert = (alert: AlertModel, suppressNotification: boolean) => {
    if (suppressNotification) this.alertsLoading = true;
    else alert['deleting'] = true;

    this._alertsService.clearAlert(alert.alertId).subscribe({
      next: () => {
        this.notifications = this.notifications.filter(
          (item) => item.alertId !== alert.alertId
        );

        this.removeAlert(alert);
        setTimeout(() => {
          this.refreshFilters();
        });

        if (!suppressNotification) {
          this._notificationService.showSuccess(
            'Notification deleted successfuly.',
            'Success!'
          );
        }
      },
      error: (err) => {
        if (!suppressNotification) {
          this._notificationService.showError(
            err?.message || "Couldn't delete notification",
            'Error!'
          );
        }
      }
    }).add(() => {
      if (suppressNotification) {
        this.alertsLoading = false;
      } else {
        alert['deleting'] = false;
        this._notificationService.showSuccess(
          'Notification deleted successfuly.',
          'Success!'
        );
      }
    })
  }

  resendEmail = (alert: AlertModel) => {
    this.alertsLoading = true;
    this._messageService.queueMessageForRetry(alert.messageModelId).subscribe(
      (res) => {
        this._notificationService.showSuccess(
          res.delivery + ' notification resent successfully.',
          'Success!'
        );
        this.deleteAlert(alert, true);
      },
      () => {
        this.alertsLoading = false;
        this._notificationService.showError(
          'Error resending notification.',
          'Error!'
        );
      },
      () => {
        this.alertsLoading = false;
      }
    );
  }

  onFilterChanged = () => {
    this.numberOfRecords = 20;
    const alerts = this.applyFilters();
    this.notifications = alerts.slice(0, this.numberOfRecords);
  }

  clearAll = () => {
    this.cleaningAll = true;

    let alertTypesToDelete: AlertType[];
    let combined: any;

    if (this.selectedAlertBuckets.length === 0) {
      alertTypesToDelete = this.data.alertBuckets.map(alert => alert.type);
      combined = combineLatest(alertTypesToDelete.map((type) => this._alertsService.clearAllByType(type)));
    } else {
      alertTypesToDelete = this.selectedAlertBuckets.map(alert => alert);
      combined = combineLatest(alertTypesToDelete.map((type) => this._alertsService.clearAllByType(type)));
    }

    combined.pipe(finalize(() => {
      this.cleaningAll = false
    })).subscribe({
      next: () => {
        this.data.alertBuckets.forEach(alert => {
          if (alertTypesToDelete.indexOf(alert.type) > -1) {
            alert.alerts = [];
          }
        })
        this.onFilterChanged();
        this.refreshFilters();
        this._notificationService.showSuccess("All alerts and notifications have been removed.", "Success!");
      },
      error: (err) => {
        this._notificationService.showError(err?.error?.message || "Couldn't clear all of the alerts and notifications.", "Error!");
      }
    })
  }

  openDetailsDialog = (notification: AlertModel) => {
    if (notification.alertType === AlertType.FailedEmailAlert) {
      this.showFailedMessageDetails(notification);
    }
    if (notification.alertType === AlertType.FailedSmsAlert) {
      this.showFailedMessageDetails(notification);
    }
  }

  private refreshFilters = () => {
    const updatedFiltersWithNewAlertCounts = this.generateSelectedBucketFiltersWithAlertCounts();
    this.availableBucketsForFiltering = this.generateAvailableBucketsForFilteringWithAlertCounts();
    this.selectedAlertBuckets = updatedFiltersWithNewAlertCounts;
  }

  private initialize = () => {
    this.availableBucketsForFiltering = this.generateAvailableBucketsForFilteringWithAlertCounts();
    const alerts = this.applyFilters();
    this.notifications = alerts.slice(0, this.numberOfRecords);
  }

  private applyFilters = (): AlertModel[] => {
    let alerts = [];
    if (this.selectedAlertBuckets.length) {
      alerts = this.data.getAlertsInBucketsOfTypes(this.selectedAlertBuckets.map(b => b));
    } else {
      this.data.alertBuckets.forEach(b => {
        alerts = alerts.concat(b.alerts);
      })
    }

    alerts.filter(alert => alert.alertText.includes("@")).forEach(alert => {
      alert.alertText = this.fixMentionDisplayText(alert.alertText);
    })

    return _.orderBy(alerts,
      ['createDate'],
      ['desc']);
  }

  private generateSelectedBucketFiltersWithAlertCounts = () => {
    const updatedFiltersWithNewAlertCounts = [];
    this.selectedAlertBuckets.forEach(sb => {
      const originalBucket = this.data.alertBuckets.find(b => b.type === sb);
      updatedFiltersWithNewAlertCounts.push(new AlertBucket(originalBucket.name + " (" + originalBucket.alerts.length + ")", originalBucket.type, originalBucket.typeId));
    });
    return updatedFiltersWithNewAlertCounts;
  }

  private generateAvailableBucketsForFilteringWithAlertCounts = () => {
    const availableBucketsForFiltering = [];
    this.data.alertBuckets.forEach(ab => {
      availableBucketsForFiltering.push(new AlertBucket(ab.name + " (" + ab.alerts.length + ")", ab.type, ab.typeId));
    });
    return availableBucketsForFiltering;
  }

  private loadMoreRecords() {
    const alerts = this.applyFilters();
    this.notifications = alerts.slice(0, this.numberOfRecords);
  }

  private removeAlert = (alertToRemove: AlertModel) => {
    const relatedBucket = this.data.alertBuckets.find(b => b.type === alertToRemove.alertType);
    if (relatedBucket) {
      const indexOfAlertToRemove = relatedBucket.alerts.findIndex(a => a.alertId === alertToRemove.alertId);
      if (indexOfAlertToRemove >= 0) {
        relatedBucket.alerts.splice(indexOfAlertToRemove, 1);
        this.numberOfAlertsChanged.emit(new NumberOfAlertsChangedEvent(alertToRemove.alertType, relatedBucket.alerts.length));
      }
    }
  }

  private showFailedMessageDetails = (notification: AlertModel) => {
    this._spinner.show();
    /** TODO should move to message service */
    this._emailService.getEmailDetail(notification.messageModelId).subscribe(details => {
      const modalRef = this._modalService.open(PreviewSentEmailDialogComponent, Constants.modalOptions.xlarge);
      modalRef.componentInstance.message = details;
    }, err => {
      this._notificationService.showError(err ? err.message || err : 'Unable to get notification details.', "Error");
    }).add(() => this._spinner.hide());
  }

  private fixMentionDisplayText = (displayText: string): string => {
    const regexp = new RegExp(/@\{(?<display>[\w ]+):(?<type>[\w ]+)(?::(?<key>[\w-])+)?\}/g);
    let results = regexp.exec(displayText);
    if (!results || results.length < 2) {
      return displayText;
    }
    const fixedDisplayText = displayText.replace(results[0], `<a class='mention'>@${results[1]}</a>`);
    return this.fixMentionDisplayText(fixedDisplayText);
  }
}

export class NumberOfAlertsChangedEvent {
  newNumber: number;
  alertType: AlertType;

  constructor(alertType: AlertType, newNumber: number) {
    this.alertType = alertType;
    this.newNumber = newNumber;
  }
}
